import SFetch from '@app/utils/sfetch'
import type { DrawerState } from '@app/application/report/types'

// Mirrors `Report::GraphLogic::GRAPH_TYPES`. Keep in sync with
// app/models/concerns/report/graph_logic.rb when chart types are added.
export const GRAPH_TYPES = ['linechart', 'piechart', 'waterfallchart'] as const
export type GraphType = typeof GRAPH_TYPES[number]
export const DEFAULT_GRAPH_TYPE: GraphType = 'linechart'

// Defaults for everything beyond the user-provided title and formula. Keys
// track Ruby-side constants:
//   - graph_type / year_type → Report::GraphLogic
//   - series_type / value_type → Report::GraphValueLogic
//   - periods → Report::GraphPeriodLogic
const TIME_SERIES_DEFAULTS = {
  graphType: DEFAULT_GRAPH_TYPE,
  yearType: 'booking_year',
  seriesType: 'line',
  valueType: '',
  periods: 'all_periods_current_year',
} as const

export interface CreateMetricInput {
  title: string
  formula: string
  graphType?: GraphType
}

export interface UpdateMetricInput extends CreateMetricInput {
  valueId: number | null
  // True only when the graph holds this one series, the shape the modal
  // creates. Then the series legend name tracks the title, as it does on
  // create. On a graph with several series the name is the full editor's to
  // set, and the modal must leave it alone.
  singleSeries: boolean
}

// What the modal can show of an existing graph. `valueCount` and `periodCount`
// are what the graph actually holds, so the modal can say that saving leaves
// the rest alone.
export interface MetricEditData {
  title: string
  formula: string
  graphType: GraphType
  valueId: number | null
  valueCount: number
  periodCount: number
}

// SFetch.json camelizes response keys, so the server's `graph_type` arrives
// as `graphType`.
interface EditResponse {
  graphType: GraphType
  title: string | null
  values: { id: number; formula: string | null }[]
  periods: { id: number }[]
}

export async function fetchMetricRequest(params: {
  reportTemplateId: number
  graphId: number
  locale: string
}): Promise<MetricEditData> {
  const url =
    window.Silverfin.Routes.edit_report_template_report_graph_path({
      report_template_id: params.reportTemplateId,
      id: params.graphId,
      company_id: parseInt(window.Silverfin.currentCompanyId, 10),
      firm_id: parseInt(window.Silverfin.currentFirmId, 10),
      _options: true,
    }) + `.json?language=${params.locale}`

  const response = await SFetch.json<EditResponse>(url)
  if (!response) {
    throw new Error('Empty metric edit response')
  }

  const first = response.values[0]
  return {
    title: response.title ?? '',
    formula: first?.formula ?? '',
    graphType: response.graphType ?? DEFAULT_GRAPH_TYPE,
    valueId: first?.id ?? null,
    valueCount: response.values.length,
    periodCount: response.periods.length,
  }
}

export async function updateMetricRequest(params: {
  reportTemplateId: number
  graphId: number
  locale: string
  input: UpdateMetricInput
}): Promise<void> {
  const url = window.Silverfin.Routes.report_template_report_graph_path({
    report_template_id: params.reportTemplateId,
    id: params.graphId,
    company_id: parseInt(window.Silverfin.currentCompanyId, 10),
    firm_id: parseInt(window.Silverfin.currentFirmId, 10),
    _options: true,
  })
  const titleField = `title_${params.locale}`
  const nameField = `name_${params.locale}`

  // Sending the existing value's id updates that series in place. Without it
  // Rails would treat the attributes as a new series and the graph would grow
  // a duplicate on every save.
  const value: Record<string, unknown> = {
    formula: params.input.formula,
  }
  if (params.input.singleSeries) value[nameField] = params.input.title
  if (params.input.valueId !== null) value.id = params.input.valueId

  const body = {
    report_graph: {
      [titleField]: params.input.title,
      graph_type: params.input.graphType ?? DEFAULT_GRAPH_TYPE,
      report_graph_values_attributes: [value],
    },
  }

  await SFetch.json(url, { method: 'PATCH', body })
}

export async function setHighlightedRequest(params: {
  reportTemplateId: number
  graphId: number
  highlighted: boolean
}): Promise<DrawerState> {
  const url = window.Silverfin.Routes.highlight_report_report_graph_path({
    report_id: params.reportTemplateId,
    id: params.graphId,
    company_id: parseInt(window.Silverfin.currentCompanyId, 10),
    firm_id: parseInt(window.Silverfin.currentFirmId, 10),
    _options: true,
  })
  const response = await SFetch.json<DrawerState>(url, {
    method: 'PATCH',
    body: { highlighted: params.highlighted },
  })
  if (!response) {
    throw new Error('Empty drawer state response')
  }
  return response
}

export async function fetchDrawerStateRequest(params: {
  reportTemplateId: number
}): Promise<DrawerState> {
  const url = window.Silverfin.Routes.drawer_state_report_report_graphs_path({
    report_id: params.reportTemplateId,
    company_id: parseInt(window.Silverfin.currentCompanyId, 10),
    firm_id: parseInt(window.Silverfin.currentFirmId, 10),
    _options: true,
  })
  const response = await SFetch.json<DrawerState>(url)
  if (!response) {
    throw new Error('Empty drawer state response')
  }
  return response
}

export async function createMetricRequest(params: {
  reportTemplateId: number
  locale: string
  input: CreateMetricInput
}): Promise<void> {
  const url = window.Silverfin.Routes.report_report_graphs_path({
    report_id: params.reportTemplateId,
    company_id: parseInt(window.Silverfin.currentCompanyId, 10),
    firm_id: parseInt(window.Silverfin.currentFirmId, 10),
    _options: true,
  })
  const titleField = `title_${params.locale}`
  const nameField = `name_${params.locale}`

  // The model requires a complete graph (title + at least one valued series + a
  // periods row). We seed defaults for everything beyond the user-provided
  // title and formula so the modal stays minimal — see #31798. Users can refine
  // the chart in the standalone editor afterwards.
  const body = {
    report_graph: {
      [titleField]: params.input.title,
      graph_type: params.input.graphType ?? TIME_SERIES_DEFAULTS.graphType,
      year_type: TIME_SERIES_DEFAULTS.yearType,
      report_graph_values_attributes: [
        {
          formula: params.input.formula,
          [nameField]: params.input.title,
          series_type: TIME_SERIES_DEFAULTS.seriesType,
          value_type: TIME_SERIES_DEFAULTS.valueType,
          position: 0,
        },
      ],
      report_graph_periods_attributes: [
        {
          periods: TIME_SERIES_DEFAULTS.periods,
          delta: false,
          position: 0,
        },
      ],
    },
  }

  await SFetch.json(url, { method: 'POST', body })
}

export async function setPinnedRequest(params: {
  reportTemplateId: number
  graphId: number
  pinned: boolean
}): Promise<DrawerState> {
  const url = window.Silverfin.Routes.pin_report_report_graph_path({
    report_id: params.reportTemplateId,
    id: params.graphId,
    company_id: parseInt(window.Silverfin.currentCompanyId, 10),
    firm_id: parseInt(window.Silverfin.currentFirmId, 10),
    _options: true,
  })
  const response = await SFetch.json<DrawerState>(url, {
    method: 'PATCH',
    body: { pinned: params.pinned },
  })
  if (!response) {
    throw new Error('Empty drawer state response')
  }
  return response
}

export async function setPinToTopRequest(params: {
  reportTemplateId: number
  pinToTop: boolean
}): Promise<void> {
  const url =
    window.Silverfin.Routes.pinned_report_path({
      id: params.reportTemplateId,
      company_id: parseInt(window.Silverfin.currentCompanyId, 10),
      firm_id: parseInt(window.Silverfin.currentFirmId, 10),
      _options: true,
    }) + '.json'
  await SFetch.json(url, {
    method: 'PATCH',
    body: { pin_to_top: params.pinToTop },
  })
}

export async function reorderHighlightedRequest(params: {
  reportTemplateId: number
  orderedIds: number[]
}): Promise<DrawerState> {
  const url =
    window.Silverfin.Routes.reorder_highlighted_report_report_graphs_path({
      report_id: params.reportTemplateId,
      company_id: parseInt(window.Silverfin.currentCompanyId, 10),
      firm_id: parseInt(window.Silverfin.currentFirmId, 10),
      _options: true,
    })
  const response = await SFetch.json<DrawerState>(url, {
    method: 'PATCH',
    body: { orderedIds: params.orderedIds },
  })
  if (!response) {
    throw new Error('Empty drawer state response')
  }
  return response
}

export async function reorderInsightRequest(params: {
  reportTemplateId: number
  orderedIds: number[]
}): Promise<DrawerState> {
  const url = window.Silverfin.Routes.reorder_insight_report_report_graphs_path(
    {
      report_id: params.reportTemplateId,
      company_id: parseInt(window.Silverfin.currentCompanyId, 10),
      firm_id: parseInt(window.Silverfin.currentFirmId, 10),
      _options: true,
    }
  )
  const response = await SFetch.json<DrawerState>(url, {
    method: 'PATCH',
    body: { orderedIds: params.orderedIds },
  })
  if (!response) {
    throw new Error('Empty drawer state response')
  }
  return response
}
